import type { RotationOffsets } from '../types';
import apiRequest from './apiRequest';
import type { ComicCaptureStyle } from './postMotionFromVideo';

export type BatchStatus =
  | 'VALIDATING'
  | 'QUEUED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'FAILED';

export type BatchExportSettings = {
  characterID: string;
  exportType: string;
  forward: string;
  up: string;
  frameRate: number;
  frameStepSize: number;
  moveInPlace: string;
  includeMesh: string;
  keyframeCleaning: string;
  faceExpression: string;
  handID: string;
  ikFeet: string;
  ikHands: string;
  rotationOffsets: RotationOffsets;
  skeletonTemplate: string;
  trimValues: string;
  skinHex: string;
};

export type BatchModel = 'SCOOT' | 'SWING' | 'COMIC' | 'CHARACTER';

/** Model-specific knobs from the batch record (comic and/or swing fields). */
export type BatchModelProperties = {
  comicModel?: 'comic3' | 'comic4';
  facialCapture?: boolean;
  footPlanting?: boolean;
  captureStyle?: ComicCaptureStyle;
  /** Legacy response field from older API deployments. */
  qualityPreset?: ComicCaptureStyle;
  numPeople?: number;
  duration?: number;
  cfgScale?: number;
};

export type BatchResponse = {
  batchID: string;
  batchName: string;
  status: BatchStatus;
  /** User-facing reason supplied when the batch status is REJECTED. */
  rejectedReason?: string;
  type: 'COMIC' | 'GENERATION' | 'MASCOT' | 'CHARACTER';
  emailSubscribers: string[];
  exportSettings: BatchExportSettings;
  model?: BatchModel;
  modelProperties?: BatchModelProperties;
  /** Resolved requested actor limits, including per-video overrides. */
  numPeopleByMediaID?: Record<string, number>;
  loop?: boolean;
  /** Requested duration of each motion in seconds. Present when a duration was selected for Swing or Scoot. */
  requestedDuration?: number;
  numTotalJobs: number;
  estimatedSecondsWaitTime: number;
  downloadURL: string | null;
  /** Character batch rigged-FBX zip freshness (Character batches only). */
  batchZipExportStatus?: 'UNAVAILABLE' | 'BUILDING' | 'STALE' | 'READY';
  /** Rigged FBX count in the current batch ZIP when export status is READY. */
  batchZipFileCount?: number;
  /** Presigned thumbnail for the most recent job in the batch, when available. */
  thumbnailURL?: string | null;
  createdAt: string;
  createdBy: string;
  /** Cartwheel user ID of the creator, when available. */
  createdByUser?: string;
};

export default async function getBatch(
  batchID: string,
  apiKey: string,
  signal?: AbortSignal,
) {
  return await apiRequest<BatchResponse>(
    'external-mogen',
    `/batch/${batchID}`,
    {
      method: 'GET',
      headers: {
        'x-api-key': apiKey,
      },
    },
    signal,
  );
}
